import {
  type Credentials,
  type DecipheredMessage,
  getConversationKey,
  sendMessage,
  useDecryptedMessages,
  useGetOrCreateConversation,
  useTypingPresence,
} from "@alice-and-bot/core";
import {
  createCanvas,
  emptyCanvas,
  readCanvas,
} from "@alice-and-bot/core/canvas";
import { CanvasView } from "@alice-and-bot/core/components";
import { useEffect, useRef, useState } from "preact/hooks";
import { builderBotPublicSignKey } from "../../constants.ts";
import { Button, Spinner, themeColors } from "./components.tsx";
import { auth, getAuth, useAuth } from "./db.ts";
import { useActiveUser } from "./loginHooks.ts";
import { usePageMeta } from "./pageMeta.ts";
import { getActiveTheme } from "./utils.ts";

export const autoLandingConversationTitle = "auto-landing";

const isRtl = (text: string) => /[\u0590-\u05FF\u0600-\u06FF]/.test(text);

const HistoryDrawer = ({
  messages,
  builderKey,
  onClose,
  isDark,
}: {
  messages: DecipheredMessage[] | null;
  builderKey: string;
  onClose: () => void;
  isDark: boolean;
}) => {
  const sorted = [...(messages ?? [])].sort(
    (a, b) => a.timestamp - b.timestamp,
  );
  return (
    <div class="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-xs">
      <div
        class="w-full max-w-lg h-full flex flex-col shadow-2xl border-l animate-slide-left overflow-hidden"
        style={{
          background: isDark ? "#0f172a" : "#ffffff",
          borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
        }}
      >
        <div
          class="px-5 py-4 border-b flex items-center justify-between select-none shrink-0"
          style={{
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
            background: isDark ? "#1e293b" : "#f8fafc",
          }}
        >
          <div class="flex items-center gap-2.5 font-semibold text-sm">
            <span class="text-base">🤖</span>
            <span>Conversation History</span>
            <span class="text-xs font-normal opacity-60">
              ({sorted.length} messages)
            </span>
          </div>
          <button
            type="button"
            class="text-sm p-1.5 rounded-lg hover:bg-surface-overlay text-text-secondary cursor-pointer"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 flex flex-col gap-3.5">
          {sorted.length === 0
            ? (
              <div class="flex-1 flex items-center justify-center text-xs opacity-50">
                No messages in history yet.
              </div>
            )
            : (
              sorted
                .filter((m) =>
                  m.type === "text" && "text" in m && m.text.trim()
                )
                .map((m) => {
                  const isBot = m.publicSignKey === builderKey;
                  const text = (m as { text: string }).text;
                  const rtl = isRtl(text);
                  return (
                    <div
                      key={m.id || String(m.timestamp)}
                      class={`flex flex-col ${
                        isBot ? "items-start" : "items-end"
                      } w-full`}
                    >
                      <div class="flex items-center gap-1.5 mb-1 px-1 text-[11px] opacity-60">
                        <span>{isBot ? "🤖 AI Builder" : "You"}</span>
                        <span>·</span>
                        <span>
                          {new Date(m.timestamp).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <div
                        dir={rtl ? "rtl" : "ltr"}
                        class={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words shadow-sm ${
                          isBot
                            ? isDark
                              ? "bg-slate-800 text-slate-100 border border-slate-700/60 rounded-tl-sm"
                              : "bg-slate-100 text-slate-900 border border-slate-200 rounded-tl-sm"
                            : "bg-accent text-white rounded-tr-sm"
                        }`}
                        style={{
                          textAlign: rtl ? "right" : "left",
                          unicodeBidi: "plaintext",
                        }}
                      >
                        {text}
                      </div>
                    </div>
                  );
                })
            )}
        </div>
      </div>
    </div>
  );
};

// The canvas is created once, by this page, for the conversation this page
// creates. Everywhere else a conversation simply has no canvas, which is what
// lets the builder agent withhold its canvas tool.
const ensureCanvas = async ({
  conversation,
  credentials,
  initialText,
}: {
  conversation: string;
  credentials: Credentials;
  initialText?: string;
}): Promise<
  { readonly ok: true } | { readonly ok: false; readonly why: string }
> => {
  const conversationKey = await getConversationKey(credentials, conversation)
    .catch((failure: unknown) => {
      console.error("canvas: no conversation key", failure);
      return undefined;
    });
  if (!conversationKey) {
    return {
      ok: false,
      why: "This conversation's key is not available yet.",
    };
  }
  const textToSet = initialText ?? emptyCanvas;
  const existing = await readCanvas({
    conversationId: conversation,
    conversationKey,
  }).catch(() => undefined);
  if (existing && existing.ok) {
    return { ok: true };
  }
  const created = await createCanvas({
    conversationId: conversation,
    conversationKey,
    credentials,
    text: textToSet,
  }).catch((failure: unknown) => {
    console.error("canvas: create failed", failure);
    return undefined;
  });
  if (created === undefined) {
    return { ok: false, why: "Creating the canvas threw." };
  }
  // A conflict just means this conversation already has one, which is the same
  // end state we were aiming for.
  if (created.ok || created.reason === "conflict") return { ok: true };
  return { ok: false, why: created.message };
};

const TextBar = (
  {
    disabled,
    onSend,
    value,
    onChange,
    placeholder,
    isDark,
  }: {
    disabled: boolean;
    onSend: (text: string) => void;
    value: string;
    onChange: (text: string) => void;
    placeholder: string;
    isDark: boolean;
  },
) => {
  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onChange("");
    onSend(text);
  };
  return (
    <form
      class="flex gap-2 items-center w-full rounded-full p-1.5 shadow-2xl backdrop-blur-md border transition-all"
      style={{
        borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
        background: isDark
          ? "rgba(15, 23, 42, 0.88)"
          : "rgba(255, 255, 255, 0.92)",
        boxShadow: isDark
          ? "0 20px 40px -10px rgba(0,0,0,0.7)"
          : "0 20px 40px -10px rgba(0,0,0,0.15)",
      }}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <input
        class="flex-1 bg-transparent rounded-full px-5 py-2.5 outline-none text-sm transition-all focus:ring-0"
        style={{
          color: isDark ? "#f1f5f9" : "#0f172a",
        }}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
      />
      <Button
        type="submit"
        disabled={disabled || value.trim().length === 0}
        className="px-6 py-2.5 rounded-full text-sm font-medium shadow-md transition-all hover:shadow-lg disabled:opacity-50 shrink-0"
      >
        {disabled
          ? (
            <div class="flex items-center gap-2">
              <Spinner className="h-4 w-4" />
              <span>Sending...</span>
            </div>
          )
          : (
            "Send"
          )}
      </Button>
    </form>
  );
};

const AutoLandingInner = (
  { credentials }: { credentials: Credentials },
) => {
  const theme = getActiveTheme();
  const isDark = theme === "dark";
  const [ready, setReady] = useState(false);
  const [sending, setSending] = useState(false);
  const [canvasError, setCanvasError] = useState<string | null>(null);
  const [promptText, setPromptText] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [conversationKey, setConversationKey] = useState<string | null>(null);
  const [dismissedBotText, setDismissedBotText] = useState<string | null>(null);
  const [userBubbleText, setUserBubbleText] = useState<string | null>(null);
  const userBubbleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [attempt, setAttempt] = useState(0);
  const creating = useRef(false);

  const showUserBubble = (text: string) => {
    setUserBubbleText(text);
    if (userBubbleTimer.current) clearTimeout(userBubbleTimer.current);
    userBubbleTimer.current = setTimeout(() => {
      setUserBubbleText(null);
    }, 6000);
  };

  const initialMessage =
    "You are on the prompt2bot auto-landing page. Generate the initial interactive UI for this user on the canvas using edit_canvas right now from context alone.\n" +
    "First, check get_all_user_bots.\n" +
    "- If this is a new visitor with no bots: start by generating an interactive choose-language interface on the canvas with buttons for English, Hebrew, etc., or introduce prompt2bot capabilities.\n" +
    "- If this user has existing bots: generate an interactive bot management dashboard on the canvas displaying their bots with configure buttons.\n" +
    "Use edit_canvas to build the UI, and keep verbal text to 1 short sentence.";

  const conversationId = useGetOrCreateConversation({
    credentials,
    participants: [builderBotPublicSignKey],
    title: autoLandingConversationTitle,
    initialMessage,
  });

  // Fetch conversation key to subscribe to messages
  useEffect(() => {
    if (!conversationId) return;
    getConversationKey(credentials, conversationId)
      .then((k) => setConversationKey(k ?? null))
      .catch(() => setConversationKey(null));
  }, [conversationId, credentials.publicSignKey]);

  // Decrypted message subscription to get the latest builder bot message
  const { messages } = useDecryptedMessages(
    null,
    conversationKey,
    conversationId,
  );

  const latestBotMsg = messages?.find(
    (m) => m.type === "text" && m.publicSignKey === builderBotPublicSignKey,
  );
  const lastBotText = latestBotMsg && "text" in latestBotMsg
    ? latestBotMsg.text
    : null;

  const { typingNames } = useTypingPresence(
    null,
    conversationId ?? "",
    credentials.publicSignKey,
    latestBotMsg?.publicSignKey ?? null,
  );
  const isBotTyping = sending || typingNames.length > 0;

  // When messages arrive from the bot, stop the sending indicator
  useEffect(() => {
    if (latestBotMsg) {
      setSending(false);
    }
  }, [latestBotMsg]);

  useEffect(() => {
    if (!conversationId) return;
    if (creating.current) return;
    creating.current = true;
    ensureCanvas({
      conversation: conversationId,
      credentials,
      initialText: emptyCanvas,
    })
      .then(
        (outcome) => {
          creating.current = false;
          setReady(outcome.ok);
          setCanvasError(outcome.ok ? null : outcome.why);
        },
        (failure: unknown) => {
          creating.current = false;
          setCanvasError(
            failure instanceof Error ? failure.message : String(failure),
          );
        },
      );
  }, [conversationId, credentials.publicSignKey, attempt]);

  const handleCanvasAction = (
    e: CustomEvent<{ action: string; data: unknown }>,
  ) => {
    const action = e.detail?.action;
    if (!action) return;
    setSending(true);
    setDismissedBotText(null);
    const friendly = action
      .replace(/^choose_lang_|^create_|^configure_/, "")
      .replace(/_/g, " ");
    showUserBubble(`Action: ${friendly}`);
  };

  if (canvasError) {
    return (
      <div class="flex items-center justify-center flex-1 p-8 text-center">
        <div class="max-w-md p-6 rounded-xl border border-danger/30 bg-surface-raised-light dark:bg-surface-raised shadow-lg">
          <p class="mb-4 text-sm font-medium text-danger">{canvasError}</p>
          <Button
            onClick={() => {
              setCanvasError(null);
              setReady(false);
              creating.current = false;
              setAttempt((n) => n + 1);
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  // Hold first paint until conversation and canvas exist
  if (!conversationId || !ready) {
    return (
      <div class="flex items-center justify-center flex-1">
        <Spinner className="h-10 w-10 text-accent" />
      </div>
    );
  }

  const send = (text: string) => {
    setSending(true);
    setDismissedBotText(null);
    showUserBubble(text);
    sendMessage({
      credentials,
      conversation: conversationId,
      message: { type: "text", text },
    })
      .catch((err) => console.error("failed to send message", err))
      .finally(() => {
        setTimeout(() => setSending(false), 20000);
      });
  };

  const colors = themeColors[theme];
  const placeholder =
    "Talk to the builder to create or configure agents, or click options on the canvas...";

  const showHoverBubble = lastBotText && lastBotText !== dismissedBotText;

  return (
    <div
      class="flex flex-col flex-1 min-h-0 w-full overflow-hidden relative"
      style={{ background: colors.surface, color: colors.text }}
    >
      {/* Canvas Viewport (Clean, Full Bleed, No Artificial Window Frame) */}
      <div class="flex-1 min-h-0 relative overflow-hidden flex flex-col">
        {/* Floating Controls: History Button in Top Right */}
        <div class="absolute top-3 right-4 z-20">
          <button
            type="button"
            class="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium border shadow-sm transition-all hover:scale-102 cursor-pointer backdrop-blur-md"
            style={{
              borderColor: isDark
                ? "rgba(255,255,255,0.15)"
                : "rgba(0,0,0,0.1)",
              background: isDark
                ? "rgba(30, 41, 59, 0.85)"
                : "rgba(255, 255, 255, 0.9)",
              color: isDark ? "#f1f5f9" : "#1e293b",
            }}
            onClick={() => setShowHistory(true)}
            title="View conversation history"
          >
            <span>📜 History</span>
          </button>
        </div>

        {/* Prominent Thinking / Typing Indicator */}
        {isBotTyping && (
          <div class="absolute top-4 left-1/2 -translate-x-1/2 z-30 pointer-events-auto animate-fade-in">
            <div
              class="flex items-center gap-2.5 px-4 py-2 rounded-full shadow-xl backdrop-blur-md border text-xs font-medium"
              style={{
                borderColor: isDark
                  ? "rgba(59, 130, 246, 0.45)"
                  : "rgba(37, 99, 235, 0.35)",
                background: isDark
                  ? "rgba(17, 24, 39, 0.94)"
                  : "rgba(255, 255, 255, 0.96)",
                color: isDark ? "#60a5fa" : "#2563eb",
                boxShadow: isDark
                  ? "0 10px 25px -5px rgba(0,0,0,0.6)"
                  : "0 10px 25px -5px rgba(0,0,0,0.15)",
              }}
            >
              <span class="text-sm animate-spin">⚙️</span>
              <span>AI Builder is thinking & updating the canvas</span>
            </div>
          </div>
        )}

        {/* Floating User Message Bubble on Right */}
        {userBubbleText && (
          <div class="absolute top-16 right-4 sm:right-8 max-w-sm z-30 pointer-events-auto transition-all duration-300 animate-fade-in">
            <div
              class="px-4 py-2.5 rounded-2xl rounded-tr-sm shadow-xl backdrop-blur-md border text-sm leading-relaxed"
              style={{
                background: isDark
                  ? "rgba(37, 99, 235, 0.9)"
                  : "rgba(37, 99, 235, 0.95)",
                borderColor: "rgba(255, 255, 255, 0.25)",
                color: "#ffffff",
                boxShadow: "0 10px 25px -5px rgba(37, 99, 235, 0.35)",
              }}
            >
              <div class="text-[10px] font-semibold opacity-75 uppercase tracking-wider mb-0.5">
                You
              </div>
              <div class="font-normal">{userBubbleText}</div>
            </div>
          </div>
        )}

        {/* Hovering Last Builder Message */}
        {showHoverBubble && !isBotTyping && (
          <div class="absolute top-16 left-1/2 -translate-x-1/2 max-w-2xl w-[92%] z-20 pointer-events-auto">
            <div
              dir={isRtl(lastBotText || "") ? "rtl" : "ltr"}
              class="flex items-start gap-3 p-3.5 sm:p-4 rounded-2xl shadow-xl backdrop-blur-md border transition-all animate-fade-in"
              style={{
                borderColor: isDark
                  ? "rgba(59, 130, 246, 0.35)"
                  : "rgba(37, 99, 235, 0.25)",
                background: isDark
                  ? "rgba(17, 24, 39, 0.92)"
                  : "rgba(255, 255, 255, 0.95)",
                boxShadow: isDark
                  ? "0 10px 25px -5px rgba(0,0,0,0.5)"
                  : "0 10px 25px -5px rgba(0,0,0,0.1)",
                unicodeBidi: "plaintext",
                textAlign: isRtl(lastBotText || "") ? "right" : "left",
              }}
            >
              <div class="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center shrink-0 text-base shadow-sm">
                🤖
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between gap-2 mb-0.5">
                  <span class="text-xs font-semibold text-accent">
                    AI Builder
                  </span>
                  <button
                    type="button"
                    class="text-xs underline text-text-secondary-light dark:text-text-secondary hover:text-accent cursor-pointer"
                    onClick={() => setShowHistory(true)}
                  >
                    View history
                  </button>
                </div>
                <p class="text-sm leading-relaxed text-text-primary-light dark:text-text-primary">
                  {lastBotText}
                </p>
              </div>
              <button
                type="button"
                class="text-xs text-text-secondary-light dark:text-text-secondary hover:text-text-primary shrink-0 cursor-pointer p-1"
                onClick={() => setDismissedBotText(lastBotText)}
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <div class="flex-1 min-h-0 relative overflow-auto pb-24">
          <CanvasView
            credentials={credentials}
            conversationId={conversationId}
            isDark={isDark}
            initialText={emptyCanvas}
            emptyMessage="The builder bot will display your dynamic agent canvas here."
            onCanvasAction={handleCanvasAction as (e: Event) => void}
          />
        </div>

        {/* Floating Bottom Composer Bar hovering over Canvas */}
        <div class="absolute bottom-5 left-1/2 -translate-x-1/2 w-full max-w-3xl px-4 z-30 pointer-events-auto">
          <TextBar
            disabled={sending}
            onSend={send}
            value={promptText}
            onChange={setPromptText}
            placeholder={placeholder}
            isDark={isDark}
          />
        </div>
      </div>

      {/* History Slide-over Drawer */}
      {showHistory && (
        <HistoryDrawer
          messages={messages}
          builderKey={builderBotPublicSignKey}
          onClose={() => setShowHistory(false)}
          isDark={isDark}
        />
      )}
    </div>
  );
};

export const AutoLanding = () => {
  usePageMeta({
    title: "Auto Landing | prompt2bot",
    description: "Build and configure AI agents live on prompt2bot.",
  });
  const authState = useAuth();
  const user = useActiveUser();
  const [error, setError] = useState<string | null>(null);
  const signingIn = useRef(false);

  // Gate on auth state settling. If the user is ALREADY authenticated (either as a
  // registered user or an existing guest), preserve their session — never wipe it.
  useEffect(() => {
    // 1. Wait if auth state is still resolving
    if (authState.isLoading) return;
    // 2. If a user is already authenticated in React state, preserve it
    if (authState.user || user?.id) return;
    // 3. If auth loading encountered an error, avoid guessing/wiping state
    if (authState.error) return;
    if (signingIn.current) return;
    signingIn.current = true;

    // 4. Double check storage before creating a guest account. If a session is already stored,
    // preserve it rather than creating a new guest identity.
    getAuth()
      .then((existingUser: unknown) => {
        if (existingUser) {
          signingIn.current = false;
          return;
        }
        return auth.signInAsGuest();
      })
      .catch((failure: Error) => {
        console.error("auto-landing guest sign-in failed:", failure);
        setError(`Could not sign you in: ${failure.message}`);
      })
      .finally(() => {
        signingIn.current = false;
      });
  }, [authState.isLoading, authState.user, authState.error, user?.id]);

  if (error) {
    return (
      <div class="flex items-center justify-center flex-1 p-8 text-center">
        <div>
          <p class="mb-2 text-danger font-medium">{error}</p>
          <Button onClick={() => globalThis.location.reload()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!user?.aliceAndBotCredentials || !user.id) {
    return (
      <div class="flex items-center justify-center flex-1">
        <Spinner className="h-10 w-10 text-accent" />
      </div>
    );
  }
  return (
    <AutoLandingInner
      credentials={user.aliceAndBotCredentials}
    />
  );
};
